import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { TextField } from "@mui/material";
import { useEffect } from "react";

import { useTranslation } from "react-i18next";

import { CreateSubTaskInfo, CreateSubTaskInfoSchema, TaskInfo } from "../schema/tasks-and-subtasks";
import { useToDoSocket } from "../../ToDo/ToDoList/providers/SocketContext";

interface CreateSubTaskInputProps {
	taskId: number;
	taskData: TaskInfo;
	onClose: () => void;
	inputRef: React.MutableRefObject<HTMLDivElement | null>;
}

export default function CreateSubTaskInput({
	taskId,
	onClose,
	inputRef,
	taskData,
}: CreateSubTaskInputProps) {
	const { t } = useTranslation();
	const ts = (key: string) => t(`to-do.${key}`);

	const { createSubTask } = useToDoSocket();

	const defaultValues = {
		taskId,
		title: "",
		description: "",
		assigneeId: taskData.assigneeId,
		status: taskData.status,
		priority: taskData.priority,
		complexityEstimate: 2,
		tagId: taskData.tagId ?? null,
	};

	const { control, handleSubmit, reset } = useForm<CreateSubTaskInfo>({
		defaultValues,
		resolver: zodResolver(CreateSubTaskInfoSchema),
	});

	const onSubmit = async (values: CreateSubTaskInfo) => {
		try {
			createSubTask(taskId, { ...values });
			reset(defaultValues);
		} catch (err) {
			console.error(err);
		} finally {
			onClose();
		}
	};

	useEffect(() => {
		const handleClickOutside = (e: MouseEvent) => {
			if (inputRef.current && !inputRef.current.contains(e.target as Node)) {
				onClose();
			}
		};

		document.addEventListener("mousedown", handleClickOutside);

		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, [onClose]);

	return (
		<Controller
			control={control}
			name="title"
			render={({ field }) => (
				<TextField
					{...field}
					fullWidth
					multiline
					inputRef={inputRef}
					label={ts("onYourMind")}
					maxRows={8}
					minRows={2}
					size="small"
					sx={{
						"& .MuiFilledInput-root": {
							backgroundColor: "white",
							"&:hover": { backgroundColor: "white" },
							"&.Mui-focused": { backgroundColor: "white" },
						},
					}}
					onKeyDown={(e) => {
						if (e.key === "Enter" && !e.shiftKey) {
							e.preventDefault();
							handleSubmit(onSubmit)();
						}
					}}
				/>
			)}
		/>
	);
}
